import type { CreateOAuthApp } from '@wf-coe/types';
import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { useApiClient } from '../lib/use-api-client.js';
import type { OAuthAppDto } from './use-oauth-apps.js';

interface CreateOAuthAppInput {
  clientId: string;
  body: CreateOAuthApp;
}

/**
 * Mutation hook for `POST /v1/clients/{clientId}/oauth-apps`. Invalidates
 * the per-client list query (`['oauth-apps', clientId]`) on success.
 *
 * Casbin gates `(oauth-app, write)` server-side — Admin / Operator
 * only; Viewer surfaces as a 403 `ApiError`.
 */
export function useCreateOAuthAppMutation(): UseMutationResult<
  OAuthAppDto,
  Error,
  CreateOAuthAppInput
> {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ clientId, body }: CreateOAuthAppInput) => {
      if (!api) throw new Error('useCreateOAuthAppMutation: api client unavailable');
      return api.post<OAuthAppDto>(`/v1/clients/${clientId}/oauth-apps`, body);
    },
    onSuccess: (_data, { clientId }) => {
      void queryClient.invalidateQueries({ queryKey: ['oauth-apps', clientId] });
    },
  });
}
